/** Single source of truth for phone, email, government ID, GSTIN and required-field checks. */

export const COUNTRIES = [
  { code: "IN", name: "India", dial: "+91", len: [10] },
  { code: "AE", name: "United Arab Emirates", dial: "+971", len: [9] },
  { code: "AF", name: "Afghanistan", dial: "+93", len: [9] },
  { code: "AU", name: "Australia", dial: "+61", len: [9] },
  { code: "BD", name: "Bangladesh", dial: "+880", len: [10] },
  { code: "BH", name: "Bahrain", dial: "+973", len: [8] },
  { code: "BR", name: "Brazil", dial: "+55", len: [10, 11] },
  { code: "BT", name: "Bhutan", dial: "+975", len: [8] },
  { code: "CA", name: "Canada", dial: "+1", len: [10] },
  { code: "CH", name: "Switzerland", dial: "+41", len: [9] },
  { code: "CN", name: "China", dial: "+86", len: [11] },
  { code: "DE", name: "Germany", dial: "+49", len: [10, 11] },
  { code: "DK", name: "Denmark", dial: "+45", len: [8] },
  { code: "EG", name: "Egypt", dial: "+20", len: [10] },
  { code: "ES", name: "Spain", dial: "+34", len: [9] },
  { code: "FR", name: "France", dial: "+33", len: [9] },
  { code: "GB", name: "United Kingdom", dial: "+44", len: [10] },
  { code: "HK", name: "Hong Kong", dial: "+852", len: [8] },
  { code: "ID", name: "Indonesia", dial: "+62", len: [9, 10, 11] },
  { code: "IE", name: "Ireland", dial: "+353", len: [9] },
  { code: "IL", name: "Israel", dial: "+972", len: [9] },
  { code: "IT", name: "Italy", dial: "+39", len: [9, 10] },
  { code: "JP", name: "Japan", dial: "+81", len: [10] },
  { code: "KE", name: "Kenya", dial: "+254", len: [9] },
  { code: "KW", name: "Kuwait", dial: "+965", len: [8] },
  { code: "LK", name: "Sri Lanka", dial: "+94", len: [9] },
  { code: "MM", name: "Myanmar", dial: "+95", len: [8, 9, 10] },
  { code: "MU", name: "Mauritius", dial: "+230", len: [8] },
  { code: "MV", name: "Maldives", dial: "+960", len: [7] },
  { code: "MY", name: "Malaysia", dial: "+60", len: [9, 10] },
  { code: "NG", name: "Nigeria", dial: "+234", len: [10] },
  { code: "NL", name: "Netherlands", dial: "+31", len: [9] },
  { code: "NP", name: "Nepal", dial: "+977", len: [10] },
  { code: "NZ", name: "New Zealand", dial: "+64", len: [8, 9] },
  { code: "OM", name: "Oman", dial: "+968", len: [8] },
  { code: "PH", name: "Philippines", dial: "+63", len: [10] },
  { code: "PK", name: "Pakistan", dial: "+92", len: [10] },
  { code: "QA", name: "Qatar", dial: "+974", len: [8] },
  { code: "RU", name: "Russia", dial: "+7", len: [10] },
  { code: "SA", name: "Saudi Arabia", dial: "+966", len: [9] },
  { code: "SE", name: "Sweden", dial: "+46", len: [9] },
  { code: "SG", name: "Singapore", dial: "+65", len: [8] },
  { code: "TH", name: "Thailand", dial: "+66", len: [9] },
  { code: "TR", name: "Türkiye", dial: "+90", len: [10] },
  { code: "TZ", name: "Tanzania", dial: "+255", len: [9] },
  { code: "UG", name: "Uganda", dial: "+256", len: [9] },
  { code: "US", name: "United States", dial: "+1", len: [10] },
  { code: "VN", name: "Vietnam", dial: "+84", len: [9] },
  { code: "ZA", name: "South Africa", dial: "+27", len: [9] },
];

export const DEFAULT_COUNTRY = COUNTRIES[0];
export const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

export const countryByCode = (code) =>
  COUNTRIES.find((c) => c.code === code) || DEFAULT_COUNTRY;

/** Best-effort split of a stored E.164 number back into country + national number. */
export const splitE164 = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return { country: DEFAULT_COUNTRY, national: "" };
  if (!raw.startsWith("+")) return { country: DEFAULT_COUNTRY, national: digitsOnly(raw).slice(-10) };
  const match = [...COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((c) => raw.startsWith(c.dial));
  if (!match) return { country: DEFAULT_COUNTRY, national: digitsOnly(raw) };
  return { country: match, national: digitsOnly(raw.slice(match.dial.length)) };
};

export const toE164 = (country, national) => {
  const n = digitsOnly(national);
  return n ? `${country.dial}${n}` : "";
};

export const validatePhone = (country, national, { required = true, label = "Mobile number" } = {}) => {
  const n = digitsOnly(national);
  if (!n) return required ? `${label} is required.` : "";
  const allowed = country.len || [];
  if (allowed.length && !allowed.includes(n.length)) {
    return `${label} must be ${allowed.join(" or ")} digits for ${country.name}.`;
  }
  if (!allowed.length && (n.length < 6 || n.length > 14)) {
    return `${label} must be between 6 and 14 digits.`;
  }
  return "";
};

export const validateEmail = (value, { required = false, label = "Email address" } = {}) => {
  const v = String(value || "").trim();
  if (!v) return required ? `${label} is required.` : "";
  return /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v) ? "" : `${label} is not valid.`;
};

export const validatePincode = (value, required = false) => {
  const v = digitsOnly(value);
  if (!v) return required ? "PIN code is required." : "";
  return /^[1-9][0-9]{5}$/.test(v) ? "" : "PIN code must be 6 digits.";
};

// ------------------------------- government IDs -------------------------------
export const ID_TYPES = [
  { value: "", label: "Not provided" },
  { value: "aadhaar", label: "Aadhaar" },
  { value: "pan", label: "PAN" },
  { value: "passport", label: "Passport" },
  { value: "driving_licence", label: "Driving Licence" },
  { value: "voter_id", label: "Voter ID (EPIC)" },
  { value: "abha", label: "ABHA" },
  { value: "other", label: "Other government ID" },
];

export const ID_PLACEHOLDER = {
  aadhaar: "1234 5678 9012",
  pan: "ABCDE1234F",
  passport: "M1234567",
  driving_licence: "MH1220110012345",
  voter_id: "ABC1234567",
  abha: "12345678901234",
  other: "ID number",
};

const D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6], [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8], [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2], [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4], [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2], [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0], [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5], [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

const verhoeffOk = (num) => {
  let c = 0;
  num.split("").reverse().forEach((ch, i) => { c = D[c][P[i % 8][Number(ch)]]; });
  return c === 0;
};

/** Live display formatting while typing. */
export const formatGovId = (type, value) => {
  const raw = String(value || "");
  if (type === "aadhaar" || type === "abha") {
    return digitsOnly(raw).slice(0, type === "aadhaar" ? 12 : 14)
      .replace(/(.{4})/g, "$1 ").trim();
  }
  if (type === "pan" || type === "voter_id") return raw.toUpperCase().replace(/\s/g, "").slice(0, 10);
  if (type === "passport" || type === "driving_licence") return raw.toUpperCase().slice(0, 20);
  return raw.toUpperCase().slice(0, 30);
};

export const normalizeGovId = (type, value) => {
  if (type === "aadhaar" || type === "abha") return digitsOnly(value);
  return String(value || "").trim().toUpperCase().replace(/\s+/g, type === "other" ? " " : "");
};

const PASSPORT_PATTERNS = { IN: /^[A-PR-WY][1-9]\d{5}[1-9]$/, US: /^[A-Z0-9]{6,9}$/, GB: /^\d{9}$/, AE: /^[A-Z0-9]{7,9}$/ };

export const validateGovId = (type, value, country = "IN") => {
  const raw = String(value || "").trim();
  if (!type && !raw) return "";
  if (raw && !type) return "Select the government ID type.";
  if (type && !raw) return "Government ID number is required.";
  const v = normalizeGovId(type, raw);
  if (type === "aadhaar") {
    if (v.length !== 12) return "Aadhaar must be exactly 12 digits.";
    if (!verhoeffOk(v)) return "This Aadhaar number fails the official checksum.";
    return "";
  }
  if (type === "pan") {
    return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v) ? "" : "PAN must look like ABCDE1234F (5 letters, 4 digits, 1 letter).";
  }
  if (type === "passport") {
    const pattern = PASSPORT_PATTERNS[(country || "IN").toUpperCase()];
    if (pattern) return pattern.test(v) ? "" : `Passport number format is not valid for ${country}.`;
    return /^[A-Z0-9]{5,12}$/.test(v) ? "" : "Passport number must be 5–12 letters or digits.";
  }
  if (type === "driving_licence") {
    if ((country || "IN").toUpperCase() === "IN") {
      const clean = v.replace(/-/g, "");
      return /^[A-Z]{2}\d{2}(19|20)\d{2}\d{7}$/.test(clean) || /^[A-Z]{2}\d{13}$/.test(clean)
        ? "" : "Indian driving licence must look like MH1220110012345.";
    }
    return /^[A-Z0-9]{5,20}$/.test(v) ? "" : "Driving licence must be 5–20 letters or digits.";
  }
  if (type === "voter_id") {
    return /^[A-Z]{3}[0-9]{7}$/.test(v) ? "" : "Voter ID must be 3 letters followed by 7 digits.";
  }
  if (type === "abha") {
    return v.length === 14 ? "" : "ABHA number must be exactly 14 digits.";
  }
  return /^[A-Z0-9 /-]{4,30}$/.test(v) ? "" : "ID number must be 4–30 letters, digits, spaces, / or -.";
};

// ------------------------------- GSTIN -------------------------------
const GST_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const validateGstin = (value, required = false) => {
  const v = String(value || "").trim().toUpperCase().replace(/\s/g, "");
  if (!v) return required ? "GSTIN is required for this business type." : "";
  if (!/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(v)) {
    return "GSTIN must be 15 characters like 22AAAAA0000A1Z5.";
  }
  let total = 0;
  for (let i = 0; i < 14; i += 1) {
    const product = GST_CHARS.indexOf(v[i]) * (i % 2 ? 2 : 1);
    total += Math.floor(product / 36) + (product % 36);
  }
  const expected = GST_CHARS[(36 - (total % 36)) % 36];
  return expected === v[14] ? "" : "This GSTIN fails the official checksum. Please re-check it.";
};

export const validateRegistrationNo = (value, label, required = true) => {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return required ? `${label} is required.` : "";
  return /^[A-Z0-9][A-Z0-9 /-]{3,29}$/.test(v) ? "" : `${label} must be 4–30 letters, digits, spaces, / or -.`;
};

export const ageFromDob = (dob) => {
  if (!dob) return "";
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age < 130 ? String(age) : "";
};

export const firstError = (errors) => Object.values(errors || {}).find(Boolean) || "";

export const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
  "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
  "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];
